import EditorJs from '@editorjs/editorjs';
import {
  Completion,
  IClientOverview,
  Identifiable,
  IMemberOverview,
  IRequest,
  IRequestOverview,
  IRequestType,
  IUserOverview,
  Nameable,
  REQUEST_NOTIFY_OPTION,
  REQUEST_STATUS,
  requestStatusLabels,
  TEMPLATE_TYPE
} from '../../../../lib/types';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { RequestTable, IRequestTableRowValues } from './request-table.component';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { RequestActionButton } from '../../../domains/request/request-action-button';
import { useFetchCurrUser } from '../../../domains/users/user.service';
import { ITableFilterOption, TableFilterKeys } from '../../../_core/table/table.types';
import { useTableFilterConfig, useTableSearchConfig } from '../../../_core/table/utils/table-utils';
import { LowlightTextWithSubtitle } from '../../../_core/typography';
import { ICreateTemplateContainerProps } from '../../../domains/template/create/create-template-form-nested.container';
import { CreateTemplateContainer } from '../../../domains/template/create/create-template-form.container';
import { useLazyRequest, useSendRequest, useUpdateRequestAssignment } from '../../../domains/request/request.service';
import toast from 'react-hot-toast';
import { showError } from '../../../../lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../../../../_routes';
import { SendRequestMessageContainer } from '../../../domains/conversation/send-request-message/send-request-message.container';
import { TableDate } from '../../../_core/table/table-date.component';
import { RequestStatusIcon } from '../../RequestPage/request-status-icon';
import { IRequestTableContainer, IRequestTableModeConfig, OnFilterChangeParams, RequestTableType } from './types';
import { sortMethodTuple, useTableSortConfig } from '../../../_core/table/utils/table-sort';
import { CompletionText } from '../../../_editor/_core/completion.component';
import { UndeliveredRequestActionButton } from '../../../domains/request/undelivered-request-action-button';
import { useDeliverRequestGroup } from '../../../domains/request-group/request-group.service';
import { useCompanyMembers } from '../../../domains/member/member.service';
import { getName } from '../../../../utils/name.utils';
import { RequestGroupTableContainer } from './request-group-table.container';

interface IRequestTableNestedContainerProps extends IRequestTableContainer, PropsWithChildren {
  favoritable?: boolean;
  hideBulk?: boolean;
  hideModeToggle?: boolean;
  initialMode?: RequestTableType;
  onFilterChange: (_: OnFilterChangeParams<IRequestOverview>) => void;
  requests?: IRequestOverview[];
  requestsLoading: boolean;
  showAllStatuses?: boolean;
}

export const RequestTableNestedContainer: React.FC<IRequestTableNestedContainerProps> = ({
  children,
  favoritable,
  hideBulk,
  hideModeToggle,
  initialMode = RequestTableType.ACTIVE,
  onFilterChange,
  requests,
  requestsLoading,
  showAllStatuses
}) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const { user } = useFetchCurrUser();
  const { members } = useCompanyMembers();
  const { getRequest, loading: gettingRequest } = useLazyRequest();

  const { deliverRequestGroup, loading: deliveringRequestGroup } = useDeliverRequestGroup();
  const { sendRequest, loading: sendingRequest } = useSendRequest();
  const { updateRequestAssignment, loading: updatingRequest } = useUpdateRequestAssignment();

  const [preppingSendRequest, setPreppingSendRequest] = useState(false);
  const [bulkIndividualRequestToSend, setBulkIndividualRequestToSend] = useState<IRequestOverview | null>(null);
  const [requestToSend, setRequestToSend] = useState<IRequest | null>(null);
  const [template, setTemplate] = useState<ICreateTemplateContainerProps | null>(null);

  const [mode, setMode] = useState<string>(initialMode);

  const [favoritedIds, setFavoritedIds] = useState<Record<string, true>>({});
  useEffect(
    () =>
      setFavoritedIds(
        requests?.reduce((acc: Record<string, true>, curr) => {
          if (curr.currStaffIsAssigned) acc[curr._id] = true;
          return acc;
        }, {}) ?? {}
      ),
    [requests]
  );

  const showingBulk = useMemo(() => !hideBulk && mode === RequestTableType.BULK, [hideBulk, mode]);
  const showingDrafts = useMemo(() => mode === RequestTableType.WORKSPACE, [mode]);

  const [searchConfig, searchMatches] = useTableSearchConfig({
    items: requests,
    keys: [
      'name',
      'company',
      'requestType',
      'company.name',
      'assignedTo.givenName',
      'assignedTo.familyName',
      'deadline',
      'sentAt',
      'status',
      'createdBy.givenName',
      'createdBy.familyName'
    ]
  });

  const handleFilterChange = useCallback(
    (filters: ITableFilterOption<IRequestOverview>[]) => onFilterChange({ filters, showingDrafts }),
    [onFilterChange, showingDrafts]
  );

  const [filterConfig, filterFunc] = useTableFilterConfig({
    filters: [
      {
        title: TableFilterKeys.STATUS,
        placeholder: 'Filter by status',
        options: showAllStatuses
          ? [
              REQUEST_STATUS.SENT,
              REQUEST_STATUS.UNDELIVERED,
              REQUEST_STATUS.CLOSED,
              REQUEST_STATUS.DRAFT,
              REQUEST_STATUS.ARCHIVED
            ]
          : showingDrafts
          ? [REQUEST_STATUS.DRAFT, REQUEST_STATUS.ARCHIVED_DRAFT]
          : [REQUEST_STATUS.SENT, REQUEST_STATUS.CLOSED, REQUEST_STATUS.ARCHIVED],
        value: showingDrafts
          ? [REQUEST_STATUS.DRAFT]
          : showAllStatuses
          ? [REQUEST_STATUS.SENT, REQUEST_STATUS.UNDELIVERED]
          : [REQUEST_STATUS.SENT],
        matcher: (curr: IRequestOverview, filterValue: string[]) => {
          let currStatus = curr.status ?? REQUEST_STATUS.DRAFT;
          if (currStatus === REQUEST_STATUS.LOCKED) currStatus = REQUEST_STATUS.SENT;

          const isDraft = currStatus === REQUEST_STATUS.DRAFT;
          if (!filterValue.length) return showAllStatuses || (showingDrafts ? isDraft : !isDraft);

          if (curr.deletedAt)
            return showAllStatuses
              ? filterValue.some((v) =>
                  [REQUEST_STATUS.ARCHIVED, REQUEST_STATUS.ARCHIVED_DRAFT].includes(v as REQUEST_STATUS)
                )
              : isDraft
              ? filterValue.includes(REQUEST_STATUS.ARCHIVED_DRAFT)
              : filterValue.includes(REQUEST_STATUS.ARCHIVED);

          return filterValue.includes(currStatus);
        },
        labeler: (value) =>
          value in requestStatusLabels ? requestStatusLabels[value as REQUEST_STATUS].title : undefined
      }
    ],
    onChange: handleFilterChange
  });

  const onCreateRequestTemplate = useCallback(
    (variables: Identifiable) => {
      getRequest(variables)
        .then(({ data }) => {
          if (data?.requestById.blocks)
            setTemplate({
              data: { ...data.requestById, blocks: data.requestById.blocks ?? [] },
              onSubmit: () => new Promise((res) => res()),
              type: TEMPLATE_TYPE.REQUEST,
              version: data.requestById.version ?? EditorJs.version
            });
          else toast.error('Failed to get request and create template');
        })
        .catch((err) => showError('Failed to load request', err));
    },
    [getRequest]
  );

  const onSendRequestTemplate = useCallback(
    (variables: Identifiable) => {
      getRequest(variables)
        .then(({ data }) => {
          if (data?.requestById) setRequestToSend(data.requestById);
          else toast.error('Failed to send request');
        })
        .catch((err) => showError('Failed to load request', err));
    },
    [getRequest]
  );

  const { sortFunc, ...restSort } = useTableSortConfig({
    sortMethods: {
      updatedAt: sortMethodTuple('date'),
      sentAt: sortMethodTuple('date'),
      name: sortMethodTuple(),
      requestType: sortMethodTuple('string', (r) => (r as IRequestType | undefined)?.type),
      completion: sortMethodTuple('default', (c) => (c as Completion | undefined)?.percentage),
      deadline: sortMethodTuple('date'),
      createdBy: sortMethodTuple('string', (c) => {
        const createdBy = c as IUserOverview;
        return getName(createdBy) ?? createdBy.email;
      }),
      assignedTo: sortMethodTuple('string', (c) => {
        const assignedTo = c as IClientOverview;
        return getName(assignedTo) ?? assignedTo.user.email;
      }),
      entity: sortMethodTuple('string', (c) => c && (c as Nameable).name),
      status: sortMethodTuple(
        'string',
        (s) => requestStatusLabels[s ? (s as REQUEST_STATUS) : REQUEST_STATUS.DRAFT].title
      )
    },
    defaultSortFunc: (a: IRequestOverview, b) => {
      if (a.deletedAt && b.deletedAt) return a.deletedAt < b.deletedAt ? 1 : -1;
      if (a.deletedAt && !b.deletedAt) return 1;
      if (!a.deletedAt && b.deletedAt) return -1;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    }
  });

  const sortedRequests = useMemo(() => {
    if (!searchMatches) return [];
    const sortable = [...searchMatches];
    return sortable.filter(filterFunc).sort(sortFunc);
  }, [searchMatches, filterFunc, sortFunc]);

  const favoritedIndices = useMemo(
    () => sortedRequests.map((r) => r._id in favoritedIds),
    [favoritedIds, sortedRequests]
  );

  const requestRows = useMemo(() => {
    const membersForUser: Record<string, IMemberOverview> = {};
    return sortedRequests.map((request, i) => {
      const row: IRequestTableRowValues = {
        favoritable: request.status !== REQUEST_STATUS.UNDELIVERED,
        request,
        values: [
          {
            children: <TableDate centered date={request.updatedAt} showTime />
          }
        ]
      };

      if (!showingDrafts)
        row.values.push({
          children: request.sentAt
            ? new Date(request.sentAt).toLocaleDateString('en-CA', {
                year: '2-digit',
                day: 'numeric',
                month: 'numeric'
              })
            : ''
        });

      row.values.push(
        {
          children: (
            <LowlightTextWithSubtitle
              subtitle={getName(request.assignedTo) ? request.assignedTo?.user.email : undefined}
            >
              {(getName(request.assignedTo) || request.assignedTo?.user.email) ?? ''}
            </LowlightTextWithSubtitle>
          )
        },
        { children: request.entity?.name ?? '' },
        { children: request.name },
        { children: request.requestType?.type ?? '' },
        {
          children: request.deadline ? <TableDate centered date={request.deadline} /> : ''
        }
      );

      if (!showingDrafts)
        row.values.push({
          children: (
            <div className="flex items-center">
              {request.clientFinishedWorking && <div className="rounded h-2 w-2 bg-success mr-2" />}
              <CompletionText completion={request.completion} />
            </div>
          )
        });

      // Gather members and use member names instead of user names in created by table
      let member = request.createdBy._id in membersForUser ? membersForUser[request.createdBy._id] : null;
      if (!member) {
        member = members?.find((m) => m.user._id === request.createdBy._id) ?? null;
        if (member) membersForUser[request.createdBy._id] = member;
      }

      row.values.push(
        {
          children: (
            <p
              className={`text-xs overflow-hidden whitespace-nowrap text-ellipsis font-bold ${
                user?._id === request.createdBy._id ? 'text-green-600' : ''
              }`}
            >
              {getName(member) || getName(request.createdBy) || request.createdBy.email}
            </p>
          )
        },
        {
          children: <RequestStatusIcon inline request={request} />
        },
        {
          children: request.editable ? (
            <RequestActionButton
              slim
              loading={gettingRequest || !!requestToSend || sendingRequest || updatingRequest}
              onCreateTemplate={() => onCreateRequestTemplate({ _id: request._id })}
              onSendRequest={() => {
                setPreppingSendRequest(true);
                onSendRequestTemplate(request);
              }}
              onResendRequest={() => {
                setPreppingSendRequest(false);
                onSendRequestTemplate(request);
              }}
              request={request}
              last={sortedRequests.length > 10 && i === sortedRequests.length - 1}
            />
          ) : request.status === REQUEST_STATUS.UNDELIVERED && request.requestGroupOverview ? (
            <UndeliveredRequestActionButton
              slim
              last={sortedRequests.length > 10 && i === sortedRequests.length - 1}
              loading={deliveringRequestGroup}
              onSendRequest={() => setBulkIndividualRequestToSend(request)}
              request={request}
              requestGroup={request.requestGroupOverview}
            />
          ) : (
            ''
          )
        }
      );

      return row;
    });
  }, [
    sortedRequests,
    showingDrafts,
    user?._id,
    gettingRequest,
    requestToSend,
    sendingRequest,
    updatingRequest,
    deliveringRequestGroup,
    members,
    onCreateRequestTemplate,
    onSendRequestTemplate
  ]);

  const handleRowDoubleClick = useCallback(
    (index: number) => {
      const request = sortedRequests[index];
      if (!request) {
        console.warn('handleRowDoubleClick: request not found at index', index);
        return;
      }

      // Prevent navigation for undelivered/pending requests
      if (request.status === REQUEST_STATUS.UNDELIVERED) {
        toast.error("This pending request hasn't been sent yet.");
        return;
      }

      navigate(ROUTE_PATHS.REQUEST + '/' + request._id, {
        state: { backPath: window.location.pathname + window.location.search }
      });
    },
    [sortedRequests, navigate]
  );

  const modeConfig: IRequestTableModeConfig = useMemo(
    () => ({
      onChange: (selected: string) => {
        setMode(selected);
        setSearchParams({ mode: selected.toLowerCase() });
        if (selected === RequestTableType.ACTIVE)
          filterConfig.setSelectedFilters({ title: TableFilterKeys.STATUS, value: [REQUEST_STATUS.SENT] });
        else if (selected === RequestTableType.BULK)
          filterConfig.setSelectedFilters({
            title: TableFilterKeys.STATUS,
            value: [REQUEST_STATUS.DRAFT, REQUEST_STATUS.SENT]
          });
        else filterConfig.setSelectedFilters({ title: TableFilterKeys.STATUS, value: [REQUEST_STATUS.DRAFT] });
      },
      options: Object.values(RequestTableType),
      value: mode
    }),
    [setSearchParams, filterConfig, mode]
  );

  if (showingBulk) return <RequestGroupTableContainer modeConfig={modeConfig} />;

  const isConfigLoading = !filterConfig.options.length;

  return (
    <>
      <RequestTable
        {...restSort}
        favoritable={favoritable}
        favorited={favoritedIndices}
        onFavorite={(index) => {
          const request = sortedRequests[index];
          updateRequestAssignment(request);
          setFavoritedIds((prev) => {
            const newFavorited = { ...prev };
            if (request._id in newFavorited) delete newFavorited[request._id];
            else newFavorited[request._id] = true;
            return newFavorited;
          });
        }}
        onRowDoubleClick={handleRowDoubleClick}
        loading={!requests || (!requests.length && requestsLoading) || isConfigLoading}
        filterConfig={filterConfig}
        requests={isConfigLoading ? [] : requestRows}
        searchConfig={searchConfig}
        draftsMode={showingDrafts}
        modeConfig={hideModeToggle ? undefined : modeConfig}
      >
        {children}
      </RequestTable>
      {!!template?.data && (
        <RIDialog open={!!template} setOpen={(o) => setTemplate((v) => (o ? v : null))}>
          <CreateTemplateContainer {...template} close={() => setTemplate(null)} data={template.data} />
        </RIDialog>
      )}
      {!!requestToSend && (
        <RIDialog setOpen={(o) => setRequestToSend((r) => (o ? r : null))}>
          <SendRequestMessageContainer
            close={() => {
              setPreppingSendRequest(false);
              setRequestToSend(null);
            }}
            request={requestToSend}
            forceSendAll={preppingSendRequest}
            onSubmit={
              preppingSendRequest
                ? ({ message, notify = [REQUEST_NOTIFY_OPTION.ALL], replyTo, subject }) =>
                    sendRequest({ _id: requestToSend._id, customMessage: message, replyTo, notify, subject })
                : undefined
            }
          />
        </RIDialog>
      )}
      {!!bulkIndividualRequestToSend && (
        <RIDialog setOpen={(o) => setBulkIndividualRequestToSend((r) => (o ? r : null))}>
          <SendRequestMessageContainer
            forceSendAll
            close={() => setBulkIndividualRequestToSend(null)}
            request={bulkIndividualRequestToSend as IRequest}
            onSubmit={({ message, replyTo, subject }) =>
              deliverRequestGroup({
                _id: bulkIndividualRequestToSend.requestGroup?._id ?? '',
                customMessage: message,
                replyTo,
                subject,
                toClients: [bulkIndividualRequestToSend.assignedTo?._id ?? '']
              })
            }
          />
        </RIDialog>
      )}
    </>
  );
};
